import fetchBusinessBudget from 'businessTraveller/actions/api/fetchBusinessBudget'
import generateBusinessBudgetKey from 'businessTraveller/utils/generateBusinessBudgetKey'
import GeoContext from 'contexts/geoContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect, useMemo } from 'react'
import config from 'constants/config'
import useQueryParams from 'hooks/useQueryParams'
import { getDestinationIdFromURLSearchParams } from 'lib/urlUtils'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'

type Params = {
  itemPolicyType: 'hotel'
  offer?: App.AnyOffer
}

function useBusinessTravellerBusinessBudgetFetcher(params: Params) {
  const {
    itemPolicyType,
    offer,
  } = params
  const { currentCurrency } = useContext(GeoContext)
  const appDispatch = useAppDispatch()
  const businessBudgets = useAppSelector(state => state.businessTraveller.businessBudgets)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const queryParams = useQueryParams()

  const {
    toBeCalculatedBusinessBudget,
    budgetKey,
  } = useMemo(() => {
    const toBeCalculatedBudget: BusinessTraveller.ToBeCalculatedBusinessBudget = {
      itemPolicyType,
      currencyCode: currentCurrency,
      lePlaceId: getDestinationIdFromURLSearchParams(queryParams),
    }

    if (offer) {
      const locationData = getLocationDataFromOffer(offer)
      toBeCalculatedBudget.longitude = locationData.location.longitude
      toBeCalculatedBudget.latitude = locationData.location.latitude
    }

    const key = generateBusinessBudgetKey(toBeCalculatedBudget)

    return {
      toBeCalculatedBusinessBudget: toBeCalculatedBudget,
      budgetKey: key,
    }
  }, [offer, itemPolicyType, currentCurrency, queryParams])

  const budgetData = budgetKey ? businessBudgets[budgetKey] : undefined

  useEffect(() => {
    if (config.businessTraveller.currentAccountMode === 'business' && businessId && toBeCalculatedBusinessBudget) {
      appDispatch(fetchBusinessBudget(businessId, [budgetKey, toBeCalculatedBusinessBudget]))
    }
  }, [appDispatch, budgetKey, businessId, toBeCalculatedBusinessBudget])

  return {
    budgetData,
    budgetKey,
    toBeCalculatedBusinessBudget,
  }
}

export default useBusinessTravellerBusinessBudgetFetcher
